import { apiFetch } from "@/lib/apiClient";

/** Document-level extraction progress. "not_started" is synthesized by the backend
 *  (never persisted) when the document exists but extraction hasn't produced a
 *  status row yet — treated identically to "in_progress" by callers here. */
export type DocumentExtractionStatus = "not_started" | "in_progress" | "complete" | "failed";

/** Per-page extraction outcome. "needs_review" means low OCR/Textract confidence —
 *  the text is still shown, just flagged. "failed" means no text could be read at all. */
export type DocumentPageStatus = "extracted" | "needs_review" | "failed";

export type DocumentPage = {
  pageNumber: number;
  status: DocumentPageStatus;
  /** 0-100, may be null (e.g. a failed page has nothing to score). */
  confidence: number | null;
  extractedText: string | null;
};

export type DocumentPagesResponse = {
  success: boolean;
  documentReferenceId: string;
  /** Null only in the "not_started" case — total isn't known yet. */
  totalPages: number | null;
  extractedPages: number;
  failedPages: number;
  lastProcessedPage: number;
  status: DocumentExtractionStatus;
  pages: DocumentPage[];
  count: number;
  hasMore: boolean;
};

export type DocumentFileResponse = {
  success: boolean;
  documentReferenceId: string;
  /** Short-lived presigned S3 GET URL to the document's own original file —
   *  PDF or image, not image-only. Signed to render inline with the file's
   *  real name. Null only if that file was never stored (its storage step
   *  failed) — that's the normal, non-error case. Use this for the download
   *  ("original") link. */
  fileUrl: string | null;
  /** Browser-renderable rendition for inline display — equals fileUrl for a
   *  PDF or a web-safe image (jpeg/png/bmp), a stored PNG for a format the
   *  browser can't show itself (TIFF), null if that PNG isn't available.
   *  Prefer this over fileUrl when actually rendering the file. */
  previewUrl: string | null;
  /** Image documents only — always null for a PDF. */
  thumbnailUrl: string | null;
  contentType: string | null;
};

/** Thrown with the real HTTP status attached, so callers can tell a permanent 404
 *  (document doesn't exist / isn't this patient's) apart from a transient failure —
 *  same pattern as UploadStatusError in dataPipelineApi.ts. */
export class DocumentApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function actingAsHeaders(actingAsProfileId?: string): Record<string, string> {
  return actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
}

/** Paginated page-level extraction results for one document, plus overall progress.
 *  Poll this while status is "not_started"/"in_progress"; stop once "complete" or "failed". */
export async function getDocumentPages(
  documentReferenceId: string,
  options?: { limit?: number; offset?: number; actingAsProfileId?: string },
): Promise<DocumentPagesResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.set("limit", String(options.limit));
  if (options?.offset) params.set("offset", String(options.offset));
  const query = params.toString();

  const { data, error, status } = await apiFetch<DocumentPagesResponse>(
    `/api/patient/documents/${documentReferenceId}/pages${query ? `?${query}` : ""}`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(options?.actingAsProfileId) },
  );

  if (error) {
    throw new DocumentApiError(status, error.message || "Failed to load document pages");
  }

  return data as DocumentPagesResponse;
}

/** The stored original file (+ thumbnail, if any) for a document — PDF or image.
 *  Returns fileUrl null only if that document's original was never stored — not an
 *  error, just nothing to show. */
export async function getDocumentFile(
  documentReferenceId: string,
  actingAsProfileId?: string,
): Promise<DocumentFileResponse> {
  const { data, error, status } = await apiFetch<DocumentFileResponse>(
    `/api/patient/documents/${documentReferenceId}/file`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) },
  );

  if (error) {
    throw new DocumentApiError(status, error.message || "Failed to load document file");
  }

  return data as DocumentFileResponse;
}

/** The stored presentedForm attachment for a DiagnosticReport — Epic's own
 *  radiology/lab report file (fetched by epic_integration/service.py::
 *  fetch_binary from DiagnosticReport.presentedForm[0]), the direct/precise
 *  counterpart to getDocumentFile's same-day-date-matched DocumentReference
 *  lookup. Same response shape and null-fileUrl-is-not-an-error contract. */
export async function getDiagnosticReportFile(
  diagnosticReportId: string,
  actingAsProfileId?: string,
): Promise<DocumentFileResponse> {
  const { data, error, status } = await apiFetch<DocumentFileResponse>(
    `/api/patient/diagnostic-reports/${diagnosticReportId}/file`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) },
  );

  if (error) {
    throw new DocumentApiError(status, error.message || "Failed to load diagnostic report file");
  }

  return data as DocumentFileResponse;
}
